import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslations } from "use-intl";

import IconClear from "@/icons/icon-clear";
import IconLens from "@/icons/icon-lens";
import { AlgoliaContext } from "~/contexts/algolia";
import { useConfiguration } from "~/contexts/configuration";
import TrackingUtils from "~/utils/tracking-utils";

import Icon from "../common/icon";
import styles from "./algolia-autocomplete-input.module.scss";

export default function AutocompleteInput() {
  const t = useTranslations();

  const inputRef = useRef(null);

  const context = useContext(AlgoliaContext);
  if (!context) {
    throw new Error("AutocompleteInput must be used within an AlgoliaContextProvider");
  }
  const { configuration } = useConfiguration();
  const { autocomplete, autocompleteState } = context;

  const [debouncedQuery, setDebouncedQuery] = useState<string | undefined>(undefined);
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setDebouncedQuery(autocompleteState?.query);
    }, 300);
    return () => {
      clearTimeout(timeOut);
    };
  }, [autocompleteState]);

  useEffect(() => {
    if (!debouncedQuery && !isFirstRender) {
      return;
    }

    const populars = autocompleteState?.collections.find((col) => col.source.sourceId === "popularPlugin");
    const products = !debouncedQuery
      ? populars
      : autocompleteState?.collections.find((col) => col.source.sourceId === "productsPlugin") ?? populars;

    if (products?.items) {
      setIsFirstRender(false);
      TrackingUtils.trackAlgoliaProductsListImpression(products.items as any, 0, "algolia_search", {
        currency: configuration.CurrencyID,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  return (
    <form
      {...(autocomplete.getFormProps({ inputElement: inputRef.current }) as any)}
      className={styles.form}
      aria-labelledby="search-label"
    >
      <label htmlFor="search-input" id="search-label" className={styles.label}>
        <Icon icon={IconLens} aria-hidden />
        <p className="sr-only">{t("a11y.searchProducts")}</p>
      </label>
      <input
        {...(autocomplete.getInputProps({} as any) as any)}
        ref={inputRef}
        id="search-input"
        type="search"
        className={styles.input}
        placeholder={t("generic.search_placeholder")}
        aria-controls="search-results"
        onKeyDown={(ev) => {
          if (ev.key == "Enter") {
            TrackingUtils.trackAlgoliaProductClick("enter", autocompleteState?.query, "none", true);
          }
        }}
      />
      {autocompleteState?.query != "" && (
        <button type="reset" className="" aria-label={t("a11y.clearSearch")}>
          <Icon icon={IconClear} aria-hidden />
        </button>
      )}
      {autocompleteState && "nbProducts" in autocompleteState.context && autocompleteState?.query != "" && (
        <div id="live-region" aria-live="polite" className="sr-only">
          {t("a11y.searchResults", { count: autocompleteState.context.nbProducts as number })}
        </div>
      )}
    </form>
  );
}
